/**
 * Persian number to words converter
 * Supports:
 * - English digits
 * - Persian digits
 * - Arabic digits
 * - Negative numbers
 * - Decimal numbers
 * - string | number input
 */

const delimiter = ' و ';
const zero = 'صفر';
const negative = 'منفی ';

const letters = [
  ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'],

  [
    'ده',
    'یازده',
    'دوازده',
    'سیزده',
    'چهارده',
    'پانزده',
    'شانزده',
    'هفده',
    'هجده',
    'نوزده',
    'بیست',
  ],

  ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'],

  [
    '',
    'صد',
    'دویست',
    'سیصد',
    'چهارصد',
    'پانصد',
    'ششصد',
    'هفتصد',
    'هشتصد',
    'نهصد',
  ],

  [
    '',
    ' هزار',
    ' میلیون',
    ' میلیارد',
    ' بیلیون',
    ' بیلیارد',
    ' تریلیون',
    ' تریلیارد',
    ' کوآدریلیون',
    ' کادریلیارد',
    ' کوینتیلیون',
    ' کوانتینیارد',
    ' سکستیلیون',
    ' سکستیلیارد',
    ' سپتیلیون',
    ' سپتیلیارد',
    ' اکتیلیون',
    ' اکتیلیارد',
    ' نانیلیون',
    ' نانیلیارد',
    ' دسیلیون',
    ' دسیلیارد',
  ],
];

const decimalSuffixes = [
  '',
  'دهم',
  'صدم',
  'هزارم',
  'ده‌هزارم',
  'صد‌هزارم',
  'میلیونوم',
  'ده‌میلیونوم',
  'صدمیلیونوم',
  'میلیاردم',
  'ده‌میلیاردم',
  'صد‌‌میلیاردم',
];

/**
 * Convert Persian + Arabic digits to English digits
 */
const normalizeDigits = (value: string): string => {
  return value
    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
};

/**
 * Split number into groups of 3 digits
 */
const prepareNumber = (num: string): string[] => {
  let out = num;

  if (out.length % 3 === 1) {
    out = `00${out}`;
  } else if (out.length % 3 === 2) {
    out = `0${out}`;
  }

  return out.replace(/\d{3}(?=\d)/g, '$&*').split('*');
};

/**
 * Convert 3-digit number to Persian words
 */
const tinyNumToWord = (num: string): string => {
  const parsedInt = parseInt(num, 10);

  if (parsedInt === 0) {
    return '';
  }

  if (parsedInt < 10) {
    return letters[0][parsedInt];
  }

  if (parsedInt <= 20) {
    return letters[1][parsedInt - 10];
  }

  if (parsedInt < 100) {
    const one = parsedInt % 10;
    const ten = Math.floor(parsedInt / 10);

    if (one > 0) {
      return letters[2][ten] + delimiter + letters[0][one];
    }

    return letters[2][ten];
  }

  const hundreds = Math.floor(parsedInt / 100);
  const remainder = parsedInt % 100;

  const out = [letters[3][hundreds]];

  if (remainder === 0) {
    return out.join(delimiter);
  }

  if (remainder < 10) {
    out.push(letters[0][remainder]);
  } else if (remainder <= 20) {
    out.push(letters[1][remainder - 10]);
  } else {
    const ten = Math.floor(remainder / 10);
    const one = remainder % 10;

    out.push(letters[2][ten]);

    if (one > 0) {
      out.push(letters[0][one]);
    }
  }

  return out.join(delimiter);
};

/**
 * Convert decimal section
 */
const convertDecimalPart = (decimalPart: string): string => {
  decimalPart = decimalPart.replace(/0*$/, '');

  if (!decimalPart) {
    return '';
  }

  if (decimalPart.length > 11) {
    decimalPart = decimalPart.slice(0, 11);
  }

  return (
    ' ممیز ' +
    Num2persian(decimalPart) +
    ' ' +
    decimalSuffixes[decimalPart.length]
  );
};

/**
 * Main function
 */
export const Num2persian = (input: string | number): string => {
  const normalizedInput = normalizeDigits(String(input)).replace(
    /[^0-9.-]/g,
    '',
  );

  const floatParse = parseFloat(normalizedInput);

  if (isNaN(floatParse)) {
    return zero;
  }

  if (floatParse === 0) {
    return zero;
  }

  const isNegative = floatParse < 0;

  const cleanInput = isNegative
    ? normalizedInput.replace(/-/g, '')
    : normalizedInput;

  let integerPart = cleanInput;
  let decimalPart = '';

  const pointIndex = cleanInput.indexOf('.');

  if (pointIndex > -1) {
    integerPart = cleanInput.substring(0, pointIndex);

    decimalPart = cleanInput.substring(pointIndex + 1);
  }

  if (integerPart.length > 66) {
    return 'خارج از محدوده';
  }

  const slicedNumber = prepareNumber(integerPart);

  const out: string[] = [];

  for (let i = 0; i < slicedNumber.length; i += 1) {
    const converted = tinyNumToWord(slicedNumber[i]);

    if (converted) {
      out.push(converted + letters[4][slicedNumber.length - (i + 1)]);
    }
  }

  const decimalText =
    decimalPart.length > 0 ? convertDecimalPart(decimalPart) : '';

  return (isNegative ? negative : '') + out.join(delimiter) + decimalText;
};

export default Num2persian;
